import { Game, TournamentGame, GameVideoSummary } from '../types';

export interface GameScoreResult {
  jetsScore: number;
  oppScore: number;
  outcome: 'W' | 'L' | 'T';
  outcomeLabel: 'Win' | 'Loss' | 'Tie';
  formattedScore: string;
  isOvertime?: boolean;
  source: 'gamesheet' | 'tournament' | 'expectedScore' | 'finalScore' | 'goals';
}

const JETS_NAMES = [
  'jets',
  'chicago jets',
  '2014 csdhl',
  'csdhl',
  "'14 chicago jets",
  "'14 jets",
  'jets 2014',
  'chicago jets 2014',
];

/**
 * Checks if a team string refers to the Chicago Jets.
 */
export function isJetsTeam(teamName?: string): boolean {
  if (!teamName) return false;
  const lower = teamName.toLowerCase().trim();
  return JETS_NAMES.some((n) => lower === n || lower.includes(n));
}

/**
 * Checks if a team string refers to the focal/target team (defaults to Chicago Jets when targetTeam is not set).
 */
export function isTargetTeam(teamName?: string | null, targetTeam?: string | null): boolean {
  if (!teamName) return false;
  const cleanTarget = (targetTeam || '').trim();
  if (!cleanTarget || isJetsTeam(cleanTarget)) {
    return isJetsTeam(teamName);
  }
  const lowerName = teamName.toLowerCase().trim();
  const lowerTarget = cleanTarget.toLowerCase();
  if (lowerName === lowerTarget || lowerName.includes(lowerTarget) || lowerTarget.includes(lowerName)) {
    return true;
  }
  return areOpponentsMatching(teamName, cleanTarget);
}

/**
 * Returns the focal team display name for a video (defaults to 'Jets' when short=true, or 'Chicago Jets' when short=false).
 */
export function getTargetTeamName(
  video?: { targetTeam?: string | null } | null,
  short: boolean = true
): string {
  const custom = (video?.targetTeam || '').trim();
  if (custom && !isJetsTeam(custom)) {
    return custom;
  }
  return short ? 'Jets' : 'Chicago Jets';
}

/**
 * Strips non-alphanumeric noise and common youth hockey keywords to extract core team moniker.
 */
export function extractTeamKeywords(name: string): string[] {
  if (!name) return [];
  const lower = name.toLowerCase().replace(/[^a-z0-9\s]/g, ' ');
  const stopWords = new Set([
    '2014',
    'csdhl',
    'hockey',
    'club',
    'hc',
    'peewee',
    'pee',
    'wee',
    'major',
    'minor',
    'aaa',
    'aa',
    'a',
    'team',
    'the',
    'at',
    'vs',
    'game',
    'chicago',
  ]);

  return lower
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 3 && !stopWords.has(w));
}

/**
 * Checks whether two opponent name representations refer to the same team.
 */
export function areOpponentsMatching(nameA: string, nameB: string): boolean {
  if (!nameA || !nameB) return false;
  const cleanA = nameA.toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanB = nameB.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!cleanA || !cleanB) return false;
  if (cleanA === cleanB || cleanA.includes(cleanB) || cleanB.includes(cleanA)) {
    return true;
  }

  const keysA = extractTeamKeywords(nameA);
  const keysB = extractTeamKeywords(nameB);
  if (keysA.length > 0 && keysB.length > 0) {
    const hasSharedKeyword = keysA.some((k) => keysB.includes(k));
    if (hasSharedKeyword) return true;
  }

  return false;
}

/**
 * Normalizes dates from various representations ("2026-10-09", "Oct 9, 2026", "2026-10-10T00:20:00Z")
 * into canonical YYYY-MM-DD format for robust comparisons.
 */
export function normalizeDateToIso(dateStr?: string): string | null {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch && !trimmed.includes('T')) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return null;
}

/**
 * Checks whether two date representations refer to the same calendar date.
 */
export function areDatesMatching(dateA?: string, dateB?: string): boolean {
  if (!dateA || !dateB) return false;
  const isoA = normalizeDateToIso(dateA);
  const isoB = normalizeDateToIso(dateB);
  if (isoA && isoB && isoA === isoB) return true;

  // Direct substring / containment check
  const subA = dateA.substring(0, 10);
  const subB = dateB.substring(0, 10);
  if (subA && subB && (subA === subB || dateA.includes(subB) || dateB.includes(subA))) {
    return true;
  }

  // Token check for month name and day number (e.g., "Oct" and "9" in "Oct 9, 2026")
  try {
    const dA = new Date(dateA);
    const dB = new Date(dateB);
    if (!isNaN(dA.getTime()) && !isNaN(dB.getTime())) {
      return (
        dA.getFullYear() === dB.getFullYear() &&
        dA.getMonth() === dB.getMonth() &&
        dA.getDate() === dB.getDate()
      );
    }
  } catch {}

  return false;
}

/**
 * Matches a game video to its corresponding official GameSheet schedule game.
 */
export function findMatchingScheduleGame(
  video: { date?: string; opponent?: string; sourceGameId?: string; targetTeam?: string; isManualDownload?: boolean; source?: string },
  scheduleGames: Game[] = []
): Game | null {
  if (!scheduleGames || scheduleGames.length === 0) return null;

  // 1. Direct ID match if available
  if (video.sourceGameId) {
    const directMatch = scheduleGames.find(
      (g) =>
        (g.id === video.sourceGameId || g.gameId === video.sourceGameId) &&
        (isTargetTeam(g.homeTeam?.name, video.targetTeam) || isTargetTeam(g.visitorTeam?.name, video.targetTeam))
    );
    if (directMatch) return directMatch;
  }

  if (video.isManualDownload || video.source === 'manual_gamesheet') {
    return null;
  }

  const vOpp = video.opponent || '';
  if (!video.date || !vOpp) return null;

  // 2. Date and Opponent matching
  const matchingGames = scheduleGames.filter((g) => {
    const dateMatch =
      areDatesMatching(video.date, g.date) ||
      areDatesMatching(video.date, g.scheduleStartTime);
    if (!dateMatch) return false;

    const isHomeFocal = isTargetTeam(g.homeTeam?.name, video.targetTeam);
    const isVisitorFocal = isTargetTeam(g.visitorTeam?.name, video.targetTeam);
    if (!isHomeFocal && !isVisitorFocal) return false;

    const oppTeamName = isHomeFocal ? g.visitorTeam?.name : g.homeTeam?.name;
    return areOpponentsMatching(vOpp, oppTeamName || '');
  });

  return matchingGames[0] || null;
}

/**
 * Matches a game video to its corresponding tournament game.
 */
export function findMatchingTournamentGame(
  video: { date?: string; opponent?: string; sourceGameId?: string; targetTeam?: string; isManualDownload?: boolean; source?: string },
  tournamentGames: TournamentGame[] = []
): TournamentGame | null {
  if (!tournamentGames || tournamentGames.length === 0) return null;
  if (video.isManualDownload || video.source === 'manual_gamesheet') return null;

  if (video.sourceGameId) {
    const directMatch = tournamentGames.find((g) => g.id === video.sourceGameId);
    if (directMatch) return directMatch;
  }

  const vOpp = video.opponent || '';
  if (!video.date || !vOpp) return null;

  const matches = tournamentGames.filter((tg) => {
    const dateMatch = areDatesMatching(video.date, tg.dateTime);
    if (!dateMatch) return false;

    const isHomeFocal = isTargetTeam(tg.homeTeam, video.targetTeam);
    const isVisitorFocal = isTargetTeam(tg.visitorTeam, video.targetTeam);
    if (!isHomeFocal && !isVisitorFocal) return false;

    const oppTeamName = isHomeFocal ? tg.visitorTeam : tg.homeTeam;
    return areOpponentsMatching(vOpp, oppTeamName || '');
  });

  return matches[0] || null;
}

/**
 * Determines whether a game represents a home game for the focal/target team (defaults to Chicago Jets).
 */
export function isJetsHomeGame(
  video?: {
    isHome?: boolean;
    targetTeam?: string;
    homeTeam?: string;
    visitorTeam?: string;
    opponent?: string;
    originalSummary?: string;
    id?: string;
    gameId?: string;
    expectedScore?: { home_team?: string; visitor_team?: string };
  } | null,
  matchedScheduleGame?: Game | null,
  matchedTournamentGame?: TournamentGame | null
): boolean {
  if (!video) return true;

  // 1. Explicit boolean flag on video
  if (typeof video.isHome === 'boolean') {
    return video.isHome;
  }

  // 1b. Explicit homeTeam / visitorTeam fields on video
  if (video.homeTeam) {
    return isTargetTeam(video.homeTeam, video.targetTeam);
  }
  if (video.visitorTeam) {
    return !isTargetTeam(video.visitorTeam, video.targetTeam);
  }

  // 2. Expected score team metadata (GameSheet / scraper)
  if (video.expectedScore?.home_team) {
    return isTargetTeam(video.expectedScore.home_team, video.targetTeam);
  }
  if (video.expectedScore?.visitor_team) {
    return !isTargetTeam(video.expectedScore.visitor_team, video.targetTeam);
  }

  // 3. Match from official GameSheet schedule game
  if (matchedScheduleGame?.homeTeam?.name) {
    return isTargetTeam(matchedScheduleGame.homeTeam.name, video.targetTeam);
  }
  if (matchedScheduleGame?.visitorTeam?.name) {
    return !isTargetTeam(matchedScheduleGame.visitorTeam.name, video.targetTeam);
  }

  // 4. Tournament game match
  if (matchedTournamentGame?.homeTeam) {
    return isTargetTeam(matchedTournamentGame.homeTeam, video.targetTeam);
  }
  if (matchedTournamentGame?.visitorTeam) {
    return !isTargetTeam(matchedTournamentGame.visitorTeam, video.targetTeam);
  }

  // 5. Game ID or summary patterns ("_at", " at ", "@")
  const idStr = (video.gameId || video.id || '').toLowerCase();
  const summaryStr = (video.originalSummary || '').toLowerCase();
  if (idStr.includes('_at') || summaryStr.includes(' at ') || summaryStr.startsWith('@')) {
    return false;
  }
  if (idStr.includes('_vs') || summaryStr.includes(' vs ') || summaryStr.includes(' vs.')) {
    return true;
  }

  // 6. Default to home
  return true;
}

/**
 * Determines whether a specific goal was scored by the focal/target team (defaults to Chicago Jets).
 */
export function isGoalByJets(
  goal: { scoringTeam?: string; isJets?: boolean },
  isJetsHome: boolean = true,
  targetTeam?: string | null
): boolean {
  if (typeof goal?.isJets === 'boolean') {
    return goal.isJets;
  }
  if (!goal?.scoringTeam) return isJetsHome;
  const rawTeam = goal.scoringTeam.trim();
  const team = rawTeam.toLowerCase();
  if (isTargetTeam(rawTeam, targetTeam)) return true;
  if (!targetTeam && isJetsTeam(team)) return true;
  if (team === 'home') return isJetsHome;
  if (team === 'guest' || team === 'away' || team === 'visitor') return !isJetsHome;
  return false;
}

/**
 * Returns user-friendly team label for a goal (focal team vs opponent name).
 */
export function getGoalTeamName(
  goal: { scoringTeam?: string; isJets?: boolean },
  isJetsHome: boolean = true,
  opponentName: string = 'Opponent',
  targetTeam?: string | null
): string {
  const focalLabel = targetTeam && !isJetsTeam(targetTeam) ? targetTeam : 'Jets';
  return isGoalByJets(goal, isJetsHome, targetTeam) ? focalLabel : (opponentName || 'Opponent');
}

/**
 * Resolves final score and outcome (win, loss, or tie) for a game video.
 */
export function resolveGameScore(
  video: GameVideoSummary,
  scheduleGames: Game[] = [],
  tournamentGames: TournamentGame[] = []
): GameScoreResult | null {
  if (!video) return null;

  const focalLabel = getTargetTeamName(video, true);
  const matchedScheduleGame = findMatchingScheduleGame(video, scheduleGames);
  const matchedTournamentGame = findMatchingTournamentGame(video, tournamentGames);
  const isJetsHome = isJetsHomeGame(video, matchedScheduleGame, matchedTournamentGame);

  // 1. Explicit pre-computed scoreResult
  if (video.scoreResult && typeof video.scoreResult.jetsScore === 'number' && typeof video.scoreResult.oppScore === 'number') {
    const { jetsScore, oppScore, outcome, outcomeLabel, isOvertime } = video.scoreResult;
    return {
      jetsScore,
      oppScore,
      outcome: outcome || (jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T'),
      outcomeLabel: outcomeLabel || (jetsScore > oppScore ? 'Win' : jetsScore < oppScore ? 'Loss' : 'Tie'),
      formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
      isOvertime: Boolean(isOvertime),
      source: 'finalScore',
    };
  }

  // 2. Official GameSheet League Schedule match (most authoritative source of truth)
  if (matchedScheduleGame?.finalScore) {
    const { homeGoals, visitorGoals } = matchedScheduleGame.finalScore;
    const isJetsHomeFromSched = isTargetTeam(matchedScheduleGame.homeTeam?.name, video.targetTeam);
    const jetsScore = isJetsHomeFromSched ? homeGoals : visitorGoals;
    const oppScore = isJetsHomeFromSched ? visitorGoals : homeGoals;
    const isOvertime = Boolean(
      matchedScheduleGame.scoresByPeriod &&
      matchedScheduleGame.scoresByPeriod.some(
        (p: { title?: string }) => p.title?.toUpperCase().includes('OT') || p.title?.toUpperCase().includes('SO')
      )
    );
    const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';

    return {
      jetsScore,
      oppScore,
      outcome,
      outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
      formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
      isOvertime,
      source: 'gamesheet',
    };
  }

  // 3. Explicit finalScore on video document
  if (video.finalScore) {
    let jetsScore: number | undefined;
    let oppScore: number | undefined;

    if (typeof video.finalScore.jetsGoals === 'number' && typeof video.finalScore.oppGoals === 'number') {
      jetsScore = video.finalScore.jetsGoals;
      oppScore = video.finalScore.oppGoals;
    } else if (typeof video.finalScore.homeGoals === 'number' && typeof video.finalScore.visitorGoals === 'number') {
      jetsScore = isJetsHome ? video.finalScore.homeGoals : video.finalScore.visitorGoals;
      oppScore = isJetsHome ? video.finalScore.visitorGoals : video.finalScore.homeGoals;
    }

    if (jetsScore !== undefined && oppScore !== undefined) {
      const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';
      return {
        jetsScore,
        oppScore,
        outcome,
        outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
        formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
        source: 'finalScore',
      };
    }
  }

  // 4. Video metadata expectedScore from backend / video worker
  if (video.expectedScore) {
    const { final_home, final_guest, home_team, visitor_team, is_overtime } = video.expectedScore;
    if (final_home !== undefined && final_guest !== undefined) {
      const isExpectedJetsHome = home_team
        ? isTargetTeam(home_team, video.targetTeam)
        : (visitor_team ? !isTargetTeam(visitor_team, video.targetTeam) : isJetsHome);
      const jetsScore = isExpectedJetsHome ? final_home : final_guest;
      const oppScore = isExpectedJetsHome ? final_guest : final_home;
      const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';

      return {
        jetsScore,
        oppScore,
        outcome,
        outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
        formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
        isOvertime: Boolean(is_overtime),
        source: 'expectedScore',
      };
    }
  }

  // 5. Scraped / Cached Tournament Game match
  if (matchedTournamentGame) {
    const hs = parseInt(matchedTournamentGame.homeScore, 10);
    const vs = parseInt(matchedTournamentGame.visitorScore, 10);
    if (!isNaN(hs) && !isNaN(vs)) {
      const isTourneyJetsHome = isTargetTeam(matchedTournamentGame.homeTeam, video.targetTeam);
      const jetsScore = isTourneyJetsHome ? hs : vs;
      const oppScore = isTourneyJetsHome ? vs : hs;
      const isOvertime =
        (matchedTournamentGame.status || '').toLowerCase().includes('ot') ||
        (matchedTournamentGame.status || '').toLowerCase().includes('so');
      const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';

      return {
        jetsScore,
        oppScore,
        outcome,
        outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
        formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
        isOvertime,
        source: 'tournament',
      };
    }
  }

  // 6. Tagged Goal Events on the video
  const goals = video.goals || [];
  if (goals.length > 0) {
    // Try to parse the final goal's scoreAfter if formatted e.g. "Jets 4 - 2 Blues" or "4 - 2"
    const lastGoal = goals[goals.length - 1];
    if (lastGoal?.scoreAfter) {
      const match = lastGoal.scoreAfter.match(/(\d+)\s*[-:]\s*(\d+)/);
      if (match) {
        const num1 = parseInt(match[1], 10);
        const num2 = parseInt(match[2], 10);
        const isOppFirst =
          Boolean(video.opponent &&
          lastGoal.scoreAfter.toLowerCase().includes(video.opponent.toLowerCase()) &&
          lastGoal.scoreAfter.toLowerCase().indexOf(video.opponent.toLowerCase()) < lastGoal.scoreAfter.indexOf('-'));

        let jetsScore: number;
        let oppScore: number;
        if (isOppFirst) {
          jetsScore = num2;
          oppScore = num1;
        } else if (
          lastGoal.scoreAfter.toLowerCase().includes('jets') ||
          lastGoal.scoreAfter.toLowerCase().includes(focalLabel.toLowerCase()) ||
          (video.opponent && lastGoal.scoreAfter.toLowerCase().includes(video.opponent.toLowerCase()))
        ) {
          jetsScore = num1;
          oppScore = num2;
        } else {
          // Plain "H-G" format (e.g., "2-1"): num1 is Home, num2 is Guest
          jetsScore = isJetsHome ? num1 : num2;
          oppScore = isJetsHome ? num2 : num1;
        }
        const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';

        return {
          jetsScore,
          oppScore,
          outcome,
          outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
          formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
          source: 'goals',
        };
      }
    }

    // Goal count tally fallback
    let jetsScore = 0;
    let oppScore = 0;
    for (const g of goals) {
      if (isGoalByJets(g, isJetsHome, video.targetTeam)) {
        jetsScore++;
      } else {
        oppScore++;
      }
    }

    if (jetsScore > 0 || oppScore > 0) {
      const outcome: 'W' | 'L' | 'T' = jetsScore > oppScore ? 'W' : jetsScore < oppScore ? 'L' : 'T';
      return {
        jetsScore,
        oppScore,
        outcome,
        outcomeLabel: outcome === 'W' ? 'Win' : outcome === 'L' ? 'Loss' : 'Tie',
        formattedScore: `${focalLabel} ${jetsScore} - ${oppScore}`,
        source: 'goals',
      };
    }
  }

  return null;
}
